import { Injectable, OnDestroy } from '@angular/core';

@Injectable()
export class EinvoicePrintService implements OnDestroy {
  private job: AbortController | undefined;

  ngOnDestroy(): void {
    this.job?.abort(new DOMException('Viewer destroyed', 'AbortError'));
  }

  async print(source: HTMLElement, title = source.ownerDocument.title): Promise<void> {
    if (this.job) {
      throw new Error('An invoice print request is already in progress.');
    }

    const job = new AbortController();
    this.job = job;
    const owner = source.ownerDocument;
    const sourceWindow = owner.defaultView;
    if (!sourceWindow) {
      this.job = undefined;
      throw new Error('The invoice window is unavailable.');
    }

    const snapshot = source.cloneNode(true) as HTMLElement;
    snapshot.hidden = false;
    snapshot.style.setProperty('display', 'block', 'important');
    snapshot.style.setProperty('zoom', '1', 'important');
    const styles = Array.from(owner.querySelectorAll<HTMLStyleElement | HTMLLinkElement>('style, link[rel="stylesheet"]'))
      .filter(style => !style.disabled)
      .map(style => style.cloneNode(true) as HTMLStyleElement | HTMLLinkElement);
    const computed = sourceWindow.getComputedStyle(source);
    const typography = ['font-family', 'font-size', 'line-height']
      .map(property => [property, computed.getPropertyValue(property)] as const);
    const frame = owner.createElement('iframe');
    frame.title = title;
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    // Keep a real layout viewport: display:none/zero-sized frames can print blank pages.
    frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;pointer-events:none;';
    const timeout = sourceWindow.setTimeout(() => {
      job.abort(new Error('Timed out preparing the invoice for printing.'));
    }, 30000);

    try {
      const loaded = this.waitForLoad(frame, job.signal);
      frame.src = 'about:blank';
      owner.body.appendChild(frame);
      await loaded;
      const printDocument = frame.contentDocument;
      const printWindow = frame.contentWindow;
      if (!printDocument || !printWindow) {
        throw new Error('The invoice print frame is unavailable.');
      }

      printDocument.documentElement.lang = owner.documentElement.lang;
      printDocument.documentElement.dir = owner.documentElement.dir;
      printDocument.title = title;
      const base = printDocument.createElement('base');
      base.href = owner.baseURI;
      printDocument.head.appendChild(base);

      const pendingStyles = styles.map(style => {
        const loaded = style.tagName === 'LINK' ? this.waitForLoad(style, job.signal) : Promise.resolve();
        printDocument.head.appendChild(style);
        return loaded;
      });
      const reset = printDocument.createElement('style');
      reset.nonce = owner.querySelector<HTMLStyleElement>('style[nonce]')?.nonce ?? '';
      reset.textContent = `
        @page { size: auto; margin: 0; }
        html, body {
          display: block !important; position: static !important;
          width: auto !important; height: auto !important;
          min-height: 0 !important; max-height: none !important;
          margin: 0 !important; padding: 0 !important; overflow: visible !important;
          background: white !important; color: black !important;
          color-scheme: light !important;
          --color-surface: white; --color-text: black;
        }
      `;
      printDocument.head.appendChild(reset);
      for (const [property, value] of typography) {
        printDocument.body.style.setProperty(property, value, 'important');
      }
      printDocument.body.appendChild(snapshot);
      const pendingImages = Array.from(printDocument.images).map(image => {
        image.loading = 'eager';
        if (image.complete) {
          return image.naturalWidth > 0 ? Promise.resolve() : Promise.reject(new Error('An invoice image could not be loaded.'));
        }
        return this.waitForLoad(image, job.signal);
      });
      await Promise.all([...pendingStyles, ...pendingImages]);
      // Trigger layout before reading fonts.ready so the cloned content's fonts are requested.
      printDocument.body.getBoundingClientRect();
      await this.abortable(printDocument.fonts.ready, job.signal);
      sourceWindow.clearTimeout(timeout);

      await new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          printWindow.removeEventListener('afterprint', finished);
          job.signal.removeEventListener('abort', cancelled);
        };
        const finished = () => {
          cleanup();
          resolve();
        };
        const cancelled = () => {
          cleanup();
          reject(job.signal.reason);
        };
        printWindow.addEventListener('afterprint', finished);
        job.signal.addEventListener('abort', cancelled, { once: true });
        try {
          job.signal.throwIfAborted();
          printWindow.focus();
          printWindow.print();
        } catch (error) {
          cleanup();
          reject(error);
        }
      });
    } finally {
      sourceWindow.clearTimeout(timeout);
      job.abort(new DOMException('Print frame released', 'AbortError'));
      frame.remove();
      this.job = undefined;
    }
  }

  private waitForLoad(element: HTMLElement, signal: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        element.removeEventListener('load', loaded);
        element.removeEventListener('error', failed);
        signal.removeEventListener('abort', cancelled);
      };
      const loaded = () => {
        cleanup();
        resolve();
      };
      const failed = () => {
        cleanup();
        reject(new Error(`Could not load a print resource (${element.tagName.toLowerCase()}).`));
      };
      const cancelled = () => {
        cleanup();
        reject(signal.reason);
      };
      if (signal.aborted) {
        reject(signal.reason);
        return;
      }
      element.addEventListener('load', loaded, { once: true });
      element.addEventListener('error', failed, { once: true });
      signal.addEventListener('abort', cancelled, { once: true });
    });
  }

  private abortable<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const cancelled = () => reject(signal.reason);
      if (signal.aborted) {
        reject(signal.reason);
        return;
      }
      signal.addEventListener('abort', cancelled, { once: true });
      promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', cancelled));
    });
  }
}
